import React, { useState, useRef, useMemo } from 'react';
import { SubfieldDefinition } from '../../types/domain';
import { ColumnDataType } from '../../types/grid';
import { PortalPopover } from '../ui/PortalPopover';
import { Button } from '../ui/Button';
import { Brackets, Copy, Check, X, Eye } from 'lucide-react';

export interface NestedObjectCellProps {
  value: any;
  dataType?: ColumnDataType;
  columnName?: string;
  subfields?: SubfieldDefinition[];
  searchHighlight?: string;
}

/**
 * Extracts a human-readable composite summary from an object
 * using schema-defined subfields or common composite patterns.
 */
function getObjectCompositeSummary(
  obj: Record<string, any>,
  subfields?: SubfieldDefinition[]
): string {
  if (!obj || typeof obj !== 'object') return '';

  const keys = Object.keys(obj).filter((k) => !k.startsWith('_'));
  if (keys.length === 0) return 'empty';

  // 1. If explicit subfields exist, extract their values in order
  if (subfields && subfields.length > 0) {
    const parts: string[] = [];
    for (const sub of subfields) {
      const val = obj[sub.key];
      if (val !== undefined && val !== null && val !== '') {
        parts.push(typeof val === 'object' ? JSON.stringify(val) : String(val));
      }
    }
    if (parts.length > 0) {
      const shown = parts.slice(0, 3).join(' · ');
      return parts.length > 3 ? `${shown} (+${parts.length - 3} more)` : shown;
    }
  }

  // 2. Common person name & age pattern (e.g. first_name, last_name, age)
  const firstName = obj.first_name || obj.firstName;
  const lastName = obj.last_name || obj.lastName;
  const name = obj.name || obj.full_name || obj.fullName;

  if (firstName && lastName) {
    let res = `${firstName} ${lastName}`;
    if (obj.age !== undefined && obj.age !== null) {
      res += ` (${obj.age})`;
    }
    return res;
  }
  if (name) {
    let res = String(name);
    if (obj.age !== undefined && obj.age !== null) {
      res += ` (${obj.age})`;
    }
    return res;
  }

  // 3. Common address pattern (e.g. city, street, state)
  if (obj.city && obj.street) {
    return `${obj.city}, ${obj.street}`;
  }
  if (obj.city && obj.country) {
    return `${obj.city}, ${obj.country}`;
  }
  if (obj.address) {
    return typeof obj.address === 'object' ? getObjectCompositeSummary(obj.address) : String(obj.address);
  }

  // 4. Common title / label / code pattern
  if (obj.title) return String(obj.title);
  if (obj.label) return String(obj.label);
  if (obj.code) return String(obj.code);

  // 5. Fallback: Take up to 2 primary scalar key-value pairs
  const pairs: string[] = [];
  for (const k of keys) {
    const val = obj[k];
    if (val !== undefined && val !== null && typeof val !== 'object') {
      pairs.push(`${k}: ${val}`);
    }
    if (pairs.length >= 2) break;
  }

  if (pairs.length > 0) {
    const remaining = keys.length - pairs.length;
    return remaining > 0 ? `${pairs.join(', ')} (+${remaining})` : pairs.join(', ');
  }

  return `${keys.length} fields`;
}

/**
 * Extracts a human-readable composite summary from an array
 */
function getArrayCompositeSummary(arr: any[], subfields?: SubfieldDefinition[]): string {
  if (!Array.isArray(arr)) return '';
  if (arr.length === 0) return 'empty';

  const first = arr[0];

  // If array of objects
  if (first && typeof first === 'object') {
    const primary =
      first.id ||
      first.name ||
      first.title ||
      first.label ||
      first.code ||
      first.product_id ||
      first.key ||
      (subfields && subfields.length > 0 && first[subfields[0].key]) ||
      getObjectCompositeSummary(first, subfields);

    const primaryText = primary ? String(primary) : '1 item';
    if (arr.length === 1) return primaryText;
    return `${primaryText} (+${arr.length - 1} more)`;
  }

  // If array of primitives (strings, numbers)
  const previews = arr.slice(0, 2).map((item) => String(item ?? ''));
  const text = previews.join(', ');
  if (arr.length > 2) {
    return `${text} (+${arr.length - 2} more)`;
  }
  return text;
}

export const NestedObjectCell: React.FC<NestedObjectCellProps> = ({
  value,
  columnName = 'Field',
  subfields,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  const isArray = Array.isArray(value);
  const isObject = value !== null && typeof value === 'object' && !isArray;

  if (value === null || value === undefined) {
    return <span className="text-cf-textMuted">—</span>;
  }

  if (!isArray && !isObject) {
    return <span className="text-xs text-cf-textPrimary">{String(value)}</span>;
  }

  // Compute composite preview text
  const compositeText = useMemo(() => {
    if (isArray) {
      return getArrayCompositeSummary(value, subfields);
    }
    if (isObject) {
      return getObjectCompositeSummary(value, subfields);
    }
    return String(value ?? '');
  }, [value, isArray, isObject, subfields]);

  const keysCount = isArray ? value.length : isObject ? Object.keys(value).length : 0;

  const handleCopyJson = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(JSON.stringify(value, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy JSON:', err);
    }
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <>
      <div
        ref={triggerRef}
        onClick={handleTriggerClick}
        className="group inline-flex items-center gap-1.5 max-w-full overflow-hidden text-xs cursor-pointer select-none py-0.5"
        title={typeof value === 'object' ? JSON.stringify(value) : String(value)}
      >
        {/* Structural Badge (Option 1) */}
        {isArray ? (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/25 font-mono text-[10px] font-semibold shrink-0 group-hover:bg-blue-500/20 transition-colors"
            title={`${value.length} items`}
          >
            <Brackets size={10} className="shrink-0" />
            <span>{keysCount}</span>
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25 font-mono text-[10px] font-semibold shrink-0 group-hover:bg-emerald-500/20 transition-colors"
            title={`${keysCount} properties`}
          >
            <span className="font-mono text-[11px] leading-none">{`{ }`}</span>
            <span>{keysCount}</span>
          </span>
        )}

        {/* Human-Readable Composite Text (Option 2) */}
        <span className="text-xs text-cf-textPrimary font-medium truncate group-hover:text-cf-orange transition-colors">
          {compositeText}
        </span>

        {/* Subtle peek affordance */}
        <span className="text-cf-textMuted opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-auto pr-1">
          <Eye size={11} />
        </span>
      </div>

      {/* Floating Peek Card (Option 1 Popover) */}
      {isOpen && (
        <PortalPopover
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          triggerRef={triggerRef}
          className="w-[320px] max-w-[92vw] shadow-xl border border-cf-border bg-cf-surface rounded-lg overflow-hidden z-50 text-xs"
        >
          {/* Popover Header */}
          <div className="flex items-center justify-between p-2.5 bg-cf-bg/80 border-b border-cf-border">
            <div className="flex items-center gap-2 overflow-hidden">
              <span className="font-semibold text-xs text-cf-textPrimary truncate">{columnName}</span>
              {isArray ? (
                <span className="px-1.5 py-0.2 rounded bg-blue-500/15 text-blue-400 text-[10px] font-mono border border-blue-500/30 shrink-0">
                  Array [{keysCount}]
                </span>
              ) : (
                <span className="px-1.5 py-0.2 rounded bg-emerald-500/15 text-emerald-400 text-[10px] font-mono border border-emerald-500/30 shrink-0">
                  Object ({keysCount})
                </span>
              )}
            </div>

            <div className="flex items-center gap-1 shrink-0">
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={handleCopyJson}
                className="h-6 px-1.5 text-[10px] text-cf-textMuted hover:text-cf-textPrimary"
                icon={copied ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
                title="Copy raw JSON"
              >
                <span>{copied ? 'Copied' : 'JSON'}</span>
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                }}
                className="text-cf-textMuted hover:text-cf-textPrimary"
                title="Close peek"
              >
                <X size={13} />
              </Button>
            </div>
          </div>

          {/* Popover Content Body */}
          <div className="max-h-[300px] overflow-y-auto">
            {isObject && (
              <div className="divide-y divide-cf-border/60">
                {Object.entries(value).length === 0 ? (
                  <div className="p-4 text-center text-cf-textMuted text-xs italic">
                    Empty object
                  </div>
                ) : (
                  Object.entries(value).map(([k, v]) => {
                    const subDef = subfields?.find((s) => s.key === k);
                    const label = subDef?.name || k.replace(/_/g, ' ');
                    return (
                      <div
                        key={k}
                        className="flex items-center justify-between py-2 px-3 hover:bg-cf-surfaceHover/50 transition-colors gap-3"
                      >
                        <div className="flex items-center shrink-0 max-w-[140px]">
                          <span className="text-xs font-medium text-cf-textPrimary">
                            {label}
                          </span>
                        </div>
                      <div className="text-right text-xs max-w-[160px] break-words text-cf-textSecondary select-text">
                        {v === null || v === undefined ? (
                          <span className="text-zinc-600 italic">null</span>
                        ) : typeof v === 'boolean' ? (
                          <span
                            className={`inline-block px-1.5 py-0.2 rounded text-[10px] font-bold ${
                              v
                                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                                : 'bg-cf-surfaceActive text-cf-textMuted border border-cf-border'
                            }`}
                          >
                            {v ? 'TRUE' : 'FALSE'}
                          </span>
                        ) : typeof v === 'object' ? (
                          <pre className="font-mono text-[10px] text-left bg-cf-bg p-1 rounded border border-cf-border overflow-x-auto">
                            {JSON.stringify(v, null, 2)}
                          </pre>
                        ) : (
                          <span className="font-mono text-[11px] text-cf-textPrimary">{String(v)}</span>
                        )}
                      </div>
                    </div>
                    );
                  })
                )}
              </div>
            )}

            {isArray && (
              <div className="p-2 space-y-2">
                {value.length === 0 ? (
                  <div className="p-4 text-center text-cf-textMuted text-xs italic">
                    Empty list
                  </div>
                ) : (
                  value.map((item, idx) => (
                    <div
                      key={idx}
                      className="p-2 rounded bg-cf-bg/70 border border-cf-border text-xs"
                    >
                      <div className="text-[10px] font-mono text-cf-textMuted uppercase mb-1 font-semibold flex items-center justify-between">
                        <span>Item #{idx + 1}</span>
                      </div>
                      {typeof item === 'object' && item !== null ? (
                        <div className="grid grid-cols-2 gap-1.5 pt-1">
                          {Object.entries(item).map(([k, v]) => {
                            const subDef = subfields?.find((s) => s.key === k);
                            const label = subDef?.name || k.replace(/_/g, ' ');
                            return (
                              <div key={k} className="overflow-hidden">
                                <span className="text-[10px] text-cf-textMuted block truncate">
                                  {label}
                                </span>
                                <span className="text-xs text-cf-textPrimary font-medium truncate block select-text">
                                  {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-cf-textPrimary font-mono text-xs select-text">
                          {String(item)}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Popover Footer */}
          <div className="p-2 bg-cf-bg/60 border-t border-cf-border text-[10px] text-cf-textMuted flex items-center justify-between">
            <span>Click row for full record details</span>
            <span className="font-mono">{keysCount} {isArray ? 'items' : 'props'}</span>
          </div>
        </PortalPopover>
      )}
    </>
  );
};
